import dayjs from "dayjs";
import styled from "styled-components";

import { BeneficiaryType, Flow, Routes } from "@ctypes";
import { useNavigateToStep, useUser } from "@hooks";
import { Translations, i18n } from "@i18n";
import { convertCentsToDollar, formatCurrency, getWeeklyRecurringTotal } from "@utils";
import { AvatarCircle, Card, Eyebrow, GhostButton, Section, SerifTitle, ThreeUp } from "./styles";
import { home } from "./tokens";

const HeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 24px;
`;

const PersonCard = styled(Card).attrs({ as: "button", type: "button" })`
  padding: 26px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-family: ${home.sans};
  color: ${home.ink};
  text-align: left;
  cursor: pointer;
`;

const PersonName = styled.div`
  font-family: ${home.serif};
  font-size: 22px;
  letter-spacing: -0.2px;
`;

const PersonValue = styled.div`
  font-size: 27px;
  font-weight: 600;
  letter-spacing: -0.8px;
  line-height: 1;
`;

const GoalTrack = styled.div`
  height: 4px;
  border-radius: 999px;
  background: ${home.fill10};
  overflow: hidden;

  div {
    height: 100%;
    border-radius: 999px;
    background: ${home.copper};
  }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-size: 12px;

  .goal {
    color: ${home.ink44};
  }
  .weekly {
    font-weight: 600;
    color: ${home.ink80};
    margin-left: auto;
  }
`;

const AddCard = styled.button`
  border-radius: 18px;
  border: 1px dashed ${home.line20};
  background: transparent;
  padding: 26px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  text-align: left;
  font-family: ${home.sans};
  color: ${home.ink};
  cursor: pointer;
  transition: background 160ms ease;

  &:hover {
    background: ${home.fill09};
  }
`;

const AddIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 1px solid ${home.line20};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const BeneficiariesSection = () => {
  const navigate = useNavigateToStep();
  const { beneficiaries } = useUser();

  if (!beneficiaries?.length) return null;

  return (
    <Section>
      <HeaderRow>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Eyebrow>{i18n.t(Translations.HomePeopleEyebrow)}</Eyebrow>
          <SerifTitle>
            {i18n.t(Translations.HomePeopleTitleLead)}{" "}
            <em>{i18n.t(Translations.HomePeopleTitleHighlight)}</em>.
          </SerifTitle>
        </div>
        <GhostButton style={{ marginLeft: "auto" }} onClick={() => navigate(Routes.Accounts)}>
          {i18n.t(Translations.HomeViewAllAccounts)}
        </GhostButton>
      </HeaderRow>

      <ThreeUp>
        {beneficiaries.map((beneficiary, index) => {
          if (!beneficiary) return null;

          const isSelf = beneficiary.type === BeneficiaryType.Self;
          const name =
            isSelf || !beneficiary.firstName ? i18n.t(Translations.HomeYou) : beneficiary.firstName;

          const goal = beneficiary.goals?.[0];

          const balance =
            beneficiary.goals?.reduce(
              (sum, g) => sum + convertCentsToDollar(g?.currentAmountInCents),
              0,
            ) ?? 0;

          const target = convertCentsToDollar(goal?.targetAmountInCents);
          const targetYear = goal?.endDate ? dayjs(goal.endDate).year() : null;
          const pct = target > 0 ? (balance / target) * 100 : null;

          const weekly = getWeeklyRecurringTotal({ beneficiary });

          return (
            <PersonCard
              key={beneficiary.id}
              onClick={() => navigate(Routes.GoalsOverview, { state: { beneficiaryId: beneficiary.id } })}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <AvatarCircle $tone={index % 2 === 0 ? "copper" : "sage"}>
                  {(beneficiary.firstName ?? name).charAt(0).toUpperCase()}
                </AvatarCircle>
                <PersonName>{name}</PersonName>
              </div>

              <PersonValue>{formatCurrency(balance, true)}</PersonValue>

              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                {target > 0 && (
                  <GoalTrack>
                    <div style={{ width: `${Math.min(Math.max(pct ?? 0, 1), 100)}%` }} />
                  </GoalTrack>
                )}
                <MetaRow>
                  {target > 0 && targetYear && (
                    <span className="goal">
                      {i18n.t(Translations.HomeGoalMeta, {
                        target: formatCurrency(target),
                        year: targetYear,
                      })}
                    </span>
                  )}
                  {weekly > 0 && (
                    <span className="weekly">
                      {i18n.t(Translations.HomePerWeek, {
                        amount: formatCurrency(weekly),
                      })}
                    </span>
                  )}
                </MetaRow>
              </div>
            </PersonCard>
          );
        })}

        <AddCard onClick={() => navigate(Routes.AddBeneficiaryType, { state: { flow: Flow.AddGoal } })}>
          <AddIcon>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              style={{ stroke: home.ink72 }}
              strokeWidth="1.7"
              strokeLinecap="round"
            >
              <path d="M12 5v14" />
              <path d="M5 12h14" />
            </svg>
          </AddIcon>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div style={{ fontFamily: home.serif, fontSize: 20 }}>
              {i18n.t(Translations.HomeAddBeneficiary)}
            </div>
            <div
              style={{
                fontSize: 13,
                lineHeight: 1.5,
                color: home.ink46,
                maxWidth: 250,
              }}
            >
              {i18n.t(Translations.HomeAddBeneficiaryBody)}
            </div>
          </div>
        </AddCard>
      </ThreeUp>
    </Section>
  );
};
